import { BrandMark } from "@/components/site/BrandMark";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useAuth } from "@/lib/auth-context";
import { supabase } from "@/integrations/supabase/client";
import { AppAuthScreen } from "@/components/app/AppAuthScreen";

type AuthSearch = { tab?: "login" | "signup"; from?: "app" };

export const Route = createFileRoute("/auth")({
  validateSearch: (search: Record<string, unknown>): AuthSearch => ({
    tab: search.tab === "signup" ? "signup" : "login",
    from: search.from === "app" ? "app" : undefined,
  }),
  head: ({ match }) => ({
    meta: [
      { name: "viewport", content: match.search.from === "app" ? "width=device-width, initial-scale=1, viewport-fit=cover" : "width=device-width, initial-scale=1" },
      { title: "Join the Academy | XAU Pro Academy" },
      { name: "robots", content: "noindex, nofollow" },
      { name: "description", content: "Sign in or create your XAU Pro Academy account to access the SMC research dashboard." },
      { property: "og:title", content: "Join the Academy | XAU Pro Academy" },
      { property: "og:description", content: "Sign in or create your XAU Pro Academy account to access the SMC research dashboard." },
      { property: "og:url", content: "https://xauproacademy.com/auth" },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
    ],
    links: [{ rel: "canonical", href: "https://xauproacademy.com/auth" }],
  }),
  component: AuthPage,
});

function GoldToast(message: string) {
  toast.success(message, {
    style: {
      background: "hsl(var(--card) / 0.95)",
      border: "1px solid hsl(var(--gold) / 0.4)",
      color: "hsl(var(--foreground))",
      boxShadow: "0 10px 40px -10px hsl(var(--gold) / 0.4)",
    },
  });
}

function AuthPage() {
  const { tab, from } = Route.useSearch();
  const home = from === "app" ? "/app" : "/dashboard";
  const navigate = useNavigate();
  const { user, loading, signIn, signUp, signInWithGoogle } = useAuth();

  const [activeTab, setActiveTab] = useState<"login" | "signup">(tab ?? "login");
  const [busy, setBusy] = useState(false);
  const [forgotOpen, setForgotOpen] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");

  // Login form
  const [loginEmail, setLoginEmail] = useState("");
  const [loginPassword, setLoginPassword] = useState("");

  // Signup form
  const [signupName, setSignupName] = useState("");
  const [signupEmail, setSignupEmail] = useState("");
  const [signupPassword, setSignupPassword] = useState("");

  useEffect(() => {
    if (!loading && user) navigate({ to: home });
  }, [loading, user, navigate, home]);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    const { error } = await signIn(loginEmail, loginPassword);
    setBusy(false);
    if (error) return toast.error(error);
    GoldToast("Welcome back");
    navigate({ to: home });
  };

  const handleSignup = async (e: React.FormEvent) => {
    e.preventDefault();
    if (signupPassword.length < 6) return toast.error("Password must be at least 6 characters");
    setBusy(true);
    const { error } = await signUp(signupEmail, signupPassword, signupName);
    setBusy(false);
    if (error) return toast.error(error);
    GoldToast("Account created, welcome to XAU Pro Academy");
    navigate({ to: home });
  };

  const handleGoogle = async () => {
    setBusy(true);
    const { error } = await signInWithGoogle();
    if (error) {
      setBusy(false);
      toast.error(error);
    }
  };

  const handleForgot = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!forgotEmail) return;
    setBusy(true);
    const { error } = await supabase.auth.resetPasswordForEmail(forgotEmail, {
      redirectTo: `${window.location.origin}/reset-password`,
    });
    setBusy(false);
    if (error) return toast.error(error.message);
    GoldToast("Check your inbox for a reset link");
    setForgotOpen(false);
    setForgotEmail("");
  };

  if (from === "app") return <AppAuthScreen initialMode={tab} />;

  return (
    <div className="relative flex min-h-screen items-center justify-center overflow-hidden bg-gradient-hero px-4 py-12">
      <h1 className="sr-only">Sign in or create your XAU Pro Academy account</h1>
      <div className="absolute inset-0 grid-bg opacity-30" />
      <div className="absolute -left-40 top-1/4 h-96 w-96 rounded-full bg-gold/10 blur-3xl" />
      <div className="absolute -right-40 bottom-1/4 h-96 w-96 rounded-full bg-emerald/10 blur-3xl" />

      <div className="relative w-full max-w-md">
        <Link to="/" className="mb-8 flex items-center justify-center gap-2 group">
          <BrandMark className="h-10 w-10" />
          <div className="font-display text-xl font-bold tracking-tight">
            XAU <span className="text-gradient-gold">Pro</span> Academy
          </div>
        </Link>

        <div className="rounded-2xl border border-gold/20 bg-card/60 p-8 shadow-2xl backdrop-blur-xl">
          <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as "login" | "signup")}>
            <TabsList className="grid w-full grid-cols-2 bg-secondary/60">
              <TabsTrigger value="login">Login</TabsTrigger>
              <TabsTrigger value="signup">Sign Up</TabsTrigger>
            </TabsList>

            <Button
              type="button"
              variant="outline"
              className="mt-6 w-full border-border/60"
              onClick={handleGoogle}
              disabled={busy}
            >
              <svg className="h-4 w-4" viewBox="0 0 24 24">
                <path fill="#EA4335" d="M12 5c1.6 0 3 .55 4.1 1.6l3-3C17.2 1.7 14.8.7 12 .7 7.4.7 3.4 3.3 1.4 7l3.5 2.7C5.9 6.9 8.7 5 12 5z" />
                <path fill="#4285F4" d="M23.3 12.3c0-.8-.1-1.6-.2-2.3H12v4.5h6.4c-.3 1.5-1.1 2.7-2.4 3.6l3.7 2.9c2.2-2 3.6-5 3.6-8.7z" />
                <path fill="#FBBC05" d="M4.9 14.3c-.2-.7-.4-1.5-.4-2.3s.1-1.6.4-2.3L1.4 7C.5 8.5 0 10.2 0 12s.5 3.5 1.4 5l3.5-2.7z" />
                <path fill="#34A853" d="M12 23.3c3.2 0 6-1.1 8-2.9l-3.7-2.9c-1 .7-2.4 1.1-4.3 1.1-3.3 0-6.1-1.9-7.1-4.7L1.4 17C3.4 20.7 7.4 23.3 12 23.3z" />
              </svg>
              Continue with Google
            </Button>

            <div className="my-6 flex items-center gap-3 text-xs uppercase tracking-wider text-muted-foreground">
              <div className="h-px flex-1 bg-border" />
              or
              <div className="h-px flex-1 bg-border" />
            </div>

            <TabsContent value="login" className="mt-0">
              <form onSubmit={handleLogin} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="login-email">Email</Label>
                  <Input
                    id="login-email"
                    type="email"
                    required
                    value={loginEmail}
                    onChange={(e) => setLoginEmail(e.target.value)}
                    placeholder="trader@example.com"
                  />
                </div>
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="login-password">Password</Label>
                    <button
                      type="button"
                      onClick={() => { setForgotEmail(loginEmail); setForgotOpen(true); }}
                      className="text-xs text-gold hover:underline"
                    >
                      Forgot password?
                    </button>
                  </div>
                  <Input
                    id="login-password"
                    type="password"
                    required
                    value={loginPassword}
                    onChange={(e) => setLoginPassword(e.target.value)}
                    placeholder="••••••••"
                  />
                </div>
                <Button type="submit" variant="gold" size="lg" className="w-full" disabled={busy}>
                  {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Sign In"}
                </Button>
                {forgotOpen && (
                  <div className="mt-4 rounded-lg border border-gold/20 bg-secondary/40 p-4 space-y-3">
                    <div className="space-y-2">
                      <Label htmlFor="forgot-email">Reset your password</Label>
                      <Input
                        id="forgot-email"
                        type="email"
                        value={forgotEmail}
                        onChange={(e) => setForgotEmail(e.target.value)}
                        placeholder="trader@example.com"
                      />
                    </div>
                    <div className="flex gap-2">
                      <Button type="button" variant="gold" size="sm" onClick={handleForgot} disabled={busy} className="flex-1">
                        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Send reset link"}
                      </Button>
                      <Button type="button" variant="outline" size="sm" onClick={() => setForgotOpen(false)}>
                        Cancel
                      </Button>
                    </div>
                  </div>
                )}
              </form>
            </TabsContent>

            <TabsContent value="signup" className="mt-0">
              <form onSubmit={handleSignup} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="signup-name">Name</Label>
                  <Input
                    id="signup-name"
                    required
                    value={signupName}
                    onChange={(e) => setSignupName(e.target.value)}
                    placeholder="Your name"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="signup-email">Email</Label>
                  <Input
                    id="signup-email"
                    type="email"
                    required
                    value={signupEmail}
                    onChange={(e) => setSignupEmail(e.target.value)}
                    placeholder="trader@example.com"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="signup-password">Password</Label>
                  <Input
                    id="signup-password"
                    type="password"
                    required
                    minLength={6}
                    value={signupPassword}
                    onChange={(e) => setSignupPassword(e.target.value)}
                    placeholder="At least 6 characters"
                  />
                </div>
                <Button type="submit" variant="gold" size="lg" className="w-full" disabled={busy}>
                  {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create Account"}
                </Button>
              </form>
            </TabsContent>
          </Tabs>
        </div>

        <p className="mt-6 text-center text-xs text-muted-foreground">
          By continuing you agree to our terms. Trading involves risk.
        </p>
      </div>
    </div>
  );
}
